// skillgenpro-frontend/src/error-framework/ErrorBoundary.jsx
//
// Today, an uncaught render-time throw anywhere in the React tree produces
// a blank white screen — confirmed while researching this framework, no
// ErrorBoundary exists anywhere in the app. This is the single highest-
// leverage piece of the whole initiative: it costs nothing on the happy
// path and guarantees the app degrades to a recoverable screen instead of
// going fully blank.
//
// React error boundaries must be class components (componentDidCatch has
// no hook equivalent) — this is the one framework file that isn't a
// function component for that reason.
//
// Usage: wrap the whole app once in App.js for a top-level safety net, and
// optionally wrap individual high-risk feature trees (e.g. the Payment
// checkout flow, the AI interview flow) so a crash there doesn't take out
// navigation/the rest of the page too — see Phase 8's "graceful
// degradation" requirement.

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

function generateClientErrorId() {
  return 'render_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    // resetKey drives the RETRY path. Clearing hasError on its own would
    // re-render the very subtree that just threw — which normally throws
    // again on the next tick and looks to the user like the button does
    // nothing. Using it as the children's React key forces a real remount,
    // so a transient cause (a bad response already superseded by a good
    // one, a race on first paint) genuinely recovers without a page reload.
    this.state = { hasError: false, errorId: null, resetKey: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true, errorId: generateClientErrorId() };
  }

  componentDidCatch(error, info) {
    const { errorId } = this.state;
    // Structured log line, mirroring the backend's errorResponse.js shape
    // (Phase 12) — this is the frontend counterpart, since there's no
    // crash-reporting SDK wired up yet to send this anywhere beyond the
    // browser console.
    try {
      // eslint-disable-next-line no-console
      console.error(
        JSON.stringify({
          level: 'error',
          errorId,
          category: 'RENDER',
          code: 'REACT_RENDER_CRASH',
          message: error && error.message,
          componentStack: info && info.componentStack,
          page: typeof window !== 'undefined' ? window.location.pathname : undefined,
          timestamp: new Date().toISOString(),
        })
      );
    } catch (logErr) {
      // Logging must never throw from inside an error boundary.
    }
  }

  handleRetry = () => {
    this.setState((s) => ({ hasError: false, errorId: null, resetKey: s.resetKey + 1 }));
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback({
          errorId: this.state.errorId,
          retry: this.handleRetry,
          reload: this.handleReload,
          goHome: this.handleGoHome,
        });
      }
      return (
        <Box
          sx={{
            minHeight: '40vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 4,
          }}
        >
          <Typography variant="h5" gutterBottom>
            Something unexpected happened
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 480 }}>
            {this.props.recoveryHint ||
              'Our engineers have already been notified. Your saved progress elsewhere in the app is unaffected — you can safely go back or reload this section.'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 3 }}>
            Error ID: {this.state.errorId}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Try again first: it is the only option that preserves the
                rest of the page's state. Reload and Go Home are the
                escalation path, not the default. */}
            <Button variant="contained" onClick={this.handleRetry}>
              Try again
            </Button>
            <Button variant="outlined" onClick={this.handleReload}>
              Reload
            </Button>
            <Button variant="text" onClick={this.handleGoHome}>
              Go Home
            </Button>
          </Box>
        </Box>
      );
    }
    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
  }
}
